import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Typography, Box, useMediaQuery, useTheme } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable, RepealableName } from '../../../../../Components';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { OwnNotes } from '../../../../../Components/OwnNotes';
import { useFilter } from '../../../../../Components/ListFilter';
import { AptorApi, IGridQueryRequest, INamedEntity, OrderDirection } from '../../../../../Api/AptorApi';
import { useAptorApi } from '../../../../../Api';
import { Query } from '@material-table/core';
import { StepButtonBar } from '../OtherRequirementWizard/StepButtonBar';
import { StepProps } from './types';
import UpdateConnectionInfo from './UpdateConnectionInfo';
import moment from 'moment';
import { RequirementsListFilter } from './CustomerRequirements.List.Filter';

interface Step4RequirementTableProps extends StepProps {
  selectedCompanyUnit?: { id: number; name: string };
  connectionAddedToReqs: Array<{ id: number; name: string; number: string }>;
  connectionRemovedFromReqs: Array<{ id: number; name: string; number: string }>;
  requirementHasConnection: (lawId: number) => boolean;
  addRequirementConnection: (law: { id: number; name: string; number: string }) => void;
  removeRequirementConnection: (law: { id: number; name: string; number: string }) => void;
  currentStepName: string;
  updateConnectionInfo: any;
  setUpdateConnectionInfo: (info: any) => void;
}

export interface IRequirement extends INamedEntity {
  description: string;
  area: string;
  changedDate?: Date;
  effectiveTo?: Date;
  companyUnits: Array<INamedEntity & { note: string | null }>;
  hasReports: boolean;
  connections: {
    units: number;
    processes: number;
    aspects: number;
    tags: number;
  };
}

const Step4RequirementTable = ({
  onNext,
  onBack,
  onCancel,
  isFirstStep,
  selectedCompanyUnit,
  connectionAddedToReqs,
  connectionRemovedFromReqs,
  requirementHasConnection,
  addRequirementConnection,
  removeRequirementConnection,
  currentStepName,
  updateConnectionInfo,
  setUpdateConnectionInfo,
}: Step4RequirementTableProps) => {
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useFilter();
  const tableRef = useRef<any>(null);
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);

  useEffect(() => {
    if (isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, isFiltersInitialized]);

  useEffect(() => {
    if (selectedCompanyUnit) {
      setFilters('companyUnit', [selectedCompanyUnit.name]);
    }
    setIsFiltersInitialized(true);
  }, [selectedCompanyUnit, setFilters]);

  const handleDataFetch = useCallback(
    async (query: Query<any>) => {
      if (!isFiltersInitialized) {
        return { data: [], page: 0, totalCount: 0 };
      }
      return await api.searchCustomerRequirements<any>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'name',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
    },
    [api, filters, isFiltersInitialized]
  );

  const check = (req: any) => {
    let isSelected = requirementHasConnection(req.id);
    return {
      tooltip: formatMessage({
        id: isSelected ? 'component.mtable.unselectTooltip' : 'component.mtable.selectTooltip',
      }),
      icon: isSelected ? 'check_box' : 'check_box_outline_blank',
      isFreeAction: true,
      onClick: () => {
        if (isSelected) {
          removeRequirementConnection(req);
        } else {
          addRequirementConnection(req);
        }
      },
      iconProps: {
        style: { color: '#c4122f' },
      },
    };
  };

  const hasChanges = connectionAddedToReqs.length > 0 || connectionRemovedFromReqs.length > 0;
 const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ width: '100%', padding: isMobile ? theme.spacing(2) : theme.spacing(3), backgroundColor: '#f7f0f1', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <Typography variant="body2" style={{ marginBottom: theme.spacing(2), fontSize: '16px' }}>
          {formatMessage({ id: 'law-portal.manage.requirement-list.update-requirement.description' })}
        </Typography>
        <RequirementsListFilter disable={true} companyUnit={selectedCompanyUnit} filters={filters} setFilter={setFilters} />
        <Box style={{ maxHeight: '500px', overflow: 'auto', marginTop: theme.spacing(2) }}>
          <MTable
            tableRef={tableRef}
            columns={[
              // { title: formatMessage({ id: 'law-portal.manage.law-list.table.number' }), field: 'number' },
              // {
              //   title: formatMessage({ id: 'law-portal.manage.law-list.table.law' }),
              //   field: 'name',
              //   render: (row) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
              // },
              // {
              //   title: formatMessage({ id: 'law-portal.manage.law-list.table.description' }),
              //   field: 'description',
              //   render: (row) => <HtmlView variant="body2" value={row.description} />,
              //   sorting: false,
              // },
              // { title: formatMessage({ id: 'law-portal.manage.law-list.table.area' }), field: 'area' },
              // {
              //   title: formatMessage({ id: 'law-portal.manage.law-list.table.notes' }),
              //   field: 'notes',
              //   render: (row) => <OwnNotes {...row} linkTo={`/law-portal/our-laws/${row.id}`} />,
              //   sorting: false,
              // },
              {
                title: formatMessage({ id: 'law-portal.manage.requirements.table.name' }),
                field: 'name',
                render: (row) => <RepealableName name={row?.name} effectiveTo={row.effectiveTo} />,
              },
              {
                title: formatMessage({ id: 'law-portal.manage.requirements.table.description' }),
                field: 'description',
                render: (data: IRequirement) => <HtmlView variant="body2" value={data.description} />,
                sorting: false,
              },
              { title: formatMessage({ id: 'law-portal.manage.requirements.table.area' }), field: 'area' },
              {
                title: formatMessage({ id: 'law-portal.manage.requirements.table.changedDate' }),
                field: 'changedDate',
                render: (row) => renderDate(row.changedDate),
              },
              {
                title: formatMessage({ id: 'law-portal.manage.requirements.table.notes' }),
                field: 'notes',
                render: (row) => <OwnNotes {...row} linkTo={`/law-portal/other-requirements/${row.id}`} />,
                sorting: false,
              },

            ]}
            actions={[check]}
            options={{ actionsColumnIndex: 0 }}
            actionLabel={formatMessage({ id: 'law-portal.manage.update-laws.connection' })}
            data={handleDataFetch}
            components={{}}
          />
        </Box>
      </div>
      {/* <UpdateConnectionInfo
      updateConnectionInfo={updateConnectionInfo}
      currentStepName={currentStepName}
      onUpdate={setUpdateConnectionInfo}
    /> */}
      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={onNext}
          nextLabel="form.next"
          nextDisabled={!hasChanges}
          formatMessage={formatMessage}
          showBack={!isFirstStep}
        />
      </Box>
    </div>
  );
};

export default Step4RequirementTable;
